import type { UserRecord, UserRole, Status } from '@/entities/user/model/userSchema';

const KEY = 'ohs-arm-users-patches';

export type UsersPatchState = {
  added: UserRecord[];
  updatedById: Record<number, Partial<UserRecord>>;
};

function emptyState(): UsersPatchState {
  return { added: [], updatedById: {} };
}

export function readUsersPatchState(): UsersPatchState {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return emptyState();
    const p = JSON.parse(raw) as UsersPatchState;
    if (!p || !Array.isArray(p.added) || !p.updatedById) return emptyState();
    return { added: p.added, updatedById: p.updatedById };
  } catch {
    return emptyState();
  }
}

export function writeUsersPatchState(state: UsersPatchState): void {
  localStorage.setItem(KEY, JSON.stringify(state));
}

export function mergeUserRecords(base: UserRecord[]): UserRecord[] {
  const st = readUsersPatchState();
  const merged = base.map((u) => {
    const patch = st.updatedById[u.id];
    if (!patch) return u;
    return { ...u, ...patch };
  });
  return [...merged, ...st.added];
}

export function addUserRecord(input: {
  name: string;
  adLogin: string;
  orgUnit: string;
  roleCode: UserRole;
  status?: Status;
}): UserRecord {
  const st = readUsersPatchState();
  const id = -Date.now();
  const roleCode = input.roleCode;
  const row: UserRecord = {
    id,
    name: input.name.trim(),
    adLogin: input.adLogin.trim(),
    orgUnit: input.orgUnit.trim(),
    status: input.status ?? 'ok',
    roleCode,
  };
  st.added.push(row);
  writeUsersPatchState(st);
  return row;
}

export function updateUserRecord(id: number, input: { name: string; orgUnit: string; roleCode: UserRole }): void {
  const st = readUsersPatchState();
  const roleCode = input.roleCode;
  const fields = {
    name: input.name.trim(),
    orgUnit: input.orgUnit.trim(),
    roleCode,
  };
  if (id < 0) {
    const idx = st.added.findIndex((r) => r.id === id);
    if (idx < 0) return;
    st.added[idx] = { ...st.added[idx], ...fields };
    writeUsersPatchState(st);
    return;
  }
  st.updatedById[id] = { ...st.updatedById[id], ...fields };
  writeUsersPatchState(st);
}
